import { useState } from 'react'
import { ChevronDown, ChevronUp, Pencil, Plus, Trash2, Users, X } from 'lucide-react'
import { rfpApi } from '@/api/client'
import { showToast } from '@/store/uiStore'
import type { RfpTeamMember } from '../types'

const EXPERTISE_OPTIONS = [
  'Sales',
  'Identity Verification',
  'KYC / Customer Onboarding',
  'KYB / Business Verification',
  'AML / Compliance',
  'Fraud Prevention',
  'Transaction Monitoring',
  'Platform / API',
  'Security / InfoSec',
  'Privacy / Legal',
  'Data & Analytics',
  'Pricing / Commercial',
  'Implementation',
  'General',
]

interface TeamManagerProps {
  members: RfpTeamMember[]
  onUpdate: () => void
}

export function TeamManager({ members, onUpdate }: TeamManagerProps) {
  const [showAdd, setShowAdd] = useState(false)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [areas, setAreas] = useState<string[]>([])
  const [description, setDescription] = useState('')
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editDescription, setEditDescription] = useState('')
  const [expandedId, setExpandedId] = useState<number | null>(null)

  const handleAdd = async () => {
    if (!name || !email) return
    await rfpApi.addTeamMember({ name, email, expertise_areas: areas, description: description.trim() || undefined })
    showToast(`${name} added to RFP team`, 'success')
    setName('')
    setEmail('')
    setAreas([])
    setDescription('')
    setShowAdd(false)
    onUpdate()
  }

  const handleDelete = async (id: number) => {
    await rfpApi.deleteTeamMember(id)
    showToast('Team member removed', 'success')
    onUpdate()
  }

  const handleEditDescription = (member: RfpTeamMember) => {
    setEditingId(member.id)
    setEditDescription(member.description || '')
  }

  const handleSaveDescription = async (id: number) => {
    await rfpApi.updateTeamMember(id, { description: editDescription.trim() })
    showToast('Description updated', 'success')
    setEditingId(null)
    onUpdate()
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users size={14} className="text-indigo-400" />
          <h3 className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider">
            RFP Team ({members.length})
          </h3>
        </div>
        <button
          onClick={() => setShowAdd(!showAdd)}
          className="flex items-center gap-1 px-2 py-1 text-xs rounded-lg bg-indigo-600/20 text-indigo-400 hover:bg-indigo-600/30 transition-colors"
        >
          {showAdd ? <X size={12} /> : <Plus size={12} />}
          {showAdd ? 'Cancel' : 'Add Member'}
        </button>
      </div>

      {showAdd && (
        <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] p-3 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Name"
              className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] px-2.5 py-1.5 text-xs text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-[var(--accent-color)]"
            />
            <input
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="Email"
              className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] px-2.5 py-1.5 text-xs text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-[var(--accent-color)]"
            />
          </div>
          <div>
            <label className="text-[10px] text-[var(--text-muted)] block mb-1">Expertise Areas</label>
            <div className="flex flex-wrap gap-1">
              {EXPERTISE_OPTIONS.map(opt => (
                <button
                  key={opt}
                  onClick={() => setAreas(areas.includes(opt) ? areas.filter(a => a !== opt) : [...areas, opt])}
                  className={`px-2 py-0.5 text-[10px] rounded-full border transition-colors ${
                    areas.includes(opt)
                      ? 'bg-indigo-600/20 text-indigo-400 border-indigo-500/30'
                      : 'border-[var(--border-color)] text-[var(--text-muted)] hover:bg-[var(--bg-tertiary)]'
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] text-[var(--text-muted)] block mb-1">Description (helps AI assign the right questions)</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="e.g. CFO responsible for financial oversight and commercial negotiations. Should only receive pricing, contract, and financial questions."
              className="w-full rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] px-2.5 py-1.5 text-xs text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-[var(--accent-color)] resize-y"
              rows={2}
            />
          </div>
          <button
            onClick={handleAdd}
            disabled={!name || !email}
            className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg bg-indigo-600 text-white hover:bg-indigo-500 transition-colors disabled:opacity-50"
          >
            <Plus size={12} />Add
          </button>
        </div>
      )}

      {members.length === 0 && !showAdd ? (
        <p className="text-xs text-[var(--text-muted)]">No team members. Add people to assign requirements to.</p>
      ) : (
        <div className="space-y-1">
          {members.map(m => (
            <div key={m.id} className="rounded-lg bg-[var(--bg-primary)] border border-[var(--border-color)]">
              <div className="flex items-center justify-between p-2">
                <div
                  className="flex-1 cursor-pointer"
                  onClick={() => setExpandedId(expandedId === m.id ? null : m.id)}
                >
                  <p className="text-xs font-medium text-[var(--text-primary)] flex items-center gap-1">
                    {m.name}
                    {expandedId === m.id ? <ChevronUp size={10} className="text-[var(--text-muted)]" /> : <ChevronDown size={10} className="text-[var(--text-muted)]" />}
                  </p>
                  <p className="text-[10px] text-[var(--text-muted)]">
                    {m.email} — {m.expertise_areas.join(', ') || 'No areas set'}
                  </p>
                </div>
                <button onClick={() => handleDelete(m.id)} className="p-1 text-[var(--text-muted)] hover:text-red-400 transition-colors">
                  <Trash2 size={12} />
                </button>
              </div>
              {expandedId === m.id && (
                <div className="px-2 pb-2 pt-1 border-t border-[var(--border-color)]">
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-[10px] text-[var(--text-muted)]">Description (used by AI for question routing)</label>
                    {editingId !== m.id && (
                      <button
                        onClick={() => handleEditDescription(m)}
                        className="flex items-center gap-0.5 text-[10px] text-indigo-400 hover:text-indigo-300 transition-colors"
                      >
                        <Pencil size={10} /> Edit
                      </button>
                    )}
                  </div>
                  {editingId === m.id ? (
                    <div className="space-y-1.5">
                      <textarea
                        value={editDescription}
                        onChange={e => setEditDescription(e.target.value)}
                        placeholder="Describe this person's role and what types of questions they should receive..."
                        className="w-full rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] px-2.5 py-1.5 text-xs text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-[var(--accent-color)] resize-y"
                        rows={2}
                      />
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => handleSaveDescription(m.id)}
                          className="px-2 py-1 text-[10px] rounded bg-indigo-600 text-white hover:bg-indigo-500 transition-colors"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="px-2 py-1 text-[10px] rounded text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-[11px] text-[var(--text-secondary)] italic">
                      {m.description || 'No description set — add one to help the AI assign the right questions to this person.'}
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
